import React, { useState, useEffect } from 'react';
import { XMarkIcon, DocumentArrowUpIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '../../contexts/LanguageContext';
import { storage, db } from '../../lib/firebase';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, addDoc, serverTimestamp, query, where, getDocs, limit, doc, setDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';

interface MissingItemUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  missingItemId: string;
  customerId: string;
  organizationId: string;
  userId: string;
  userName: string;
  onUploadComplete: (uploadedFileUrls: string[]) => void;
}

const MissingItemUploadModal: React.FC<MissingItemUploadModalProps> = ({
  isOpen,
  onClose,
  missingItemId,
  customerId,
  organizationId,
  userId,
  userName,
  onUploadComplete
}) => {
  const { t } = useLanguage();
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [isDragging, setIsDragging] = useState(false);
  const [defaultCategoryId, setDefaultCategoryId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && organizationId) {
      loadDefaultCategory();
    }
  }, [isOpen, organizationId]);

  const loadDefaultCategory = async () => {
    try {
      // Keresünk egy "Bizonylat" vagy "Egyéb" kategóriát
      const q = query(
        collection(db, 'documentStorageCategories'),
        where('organizationId', '==', organizationId),
        where('isActive', '==', true),
        limit(1)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        setDefaultCategoryId(snapshot.docs[0].id);
      }
    } catch (error) {
      console.error('Error loading default category:', error);
    }
  };

  const ensureTagsExist = async (tags: string[]) => {
    try {
      for (const tagName of tags) {
        // Ellenőrizzük, hogy létezik-e már a tag
        const tagId = tagName.toLowerCase().replace(/\s+/g, '-');
        const tagRef = doc(db, 'documentTags', tagId);
        
        // Létrehozzuk a taget, ha még nem létezik (merge: true csak akkor ír, ha nincs)
        await setDoc(tagRef, {
          name: tagName,
          title: tagName,
          organizationId: organizationId,
          count: 0,
          color: '#3b82f6', // Default blue color
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
    } catch (error) {
      console.error('Error ensuring tags exist:', error);
    }
  };

  if (!isOpen) return null;

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      setFiles(prev => [...prev, ...droppedFiles]);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error('Kérlek válassz ki legalább egy fájlt');
      return;
    }

    setUploading(true);
    const uploadedFiles: string[] = [];

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const fileName = `${Date.now()}_${file.name}`;
        const storageRef = ref(storage, `missing-items/${organizationId}/${customerId}/${fileName}`);
        
        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise<void>((resolve, reject) => {
          uploadTask.on(
            'state_changed',
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(prev => ({ ...prev, [file.name]: progress }));
            },
            (error) => {
              console.error('Upload error:', error);
              reject(error);
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                uploadedFiles.push(downloadURL);

                // Create document in documentStorage collection only if we have a valid category
                if (defaultCategoryId) {
                  // Remove file extension for cleaner title
                  const fileNameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                  const documentTags = ['hiányzó-bizonylat', 'ügyfél-feltöltés'];
                  
                  // Ensure tags exist in documentTags collection
                  await ensureTagsExist(documentTags);
                  
                  await addDoc(collection(db, 'documentStorage'), {
                    title: fileNameWithoutExt,
                    description: 'Ügyfél által feltöltött hiányzó bizonylat',
                    categoryId: defaultCategoryId,
                    customerId: customerId,
                    customerName: '', // Will be populated by the service
                    filePath: uploadTask.snapshot.ref.fullPath,
                    fileSize: file.size,
                    fileType: file.type,
                    fileName: file.name,
                    downloadUrl: downloadURL,
                    tags: documentTags,
                    isPublic: false,
                    version: 1,
                    status: 'active',
                    createdBy: userId,
                    createdByName: userName,
                    organizationId: organizationId,
                    metadata: {
                      missingItemId: missingItemId,
                      uploadedByCustomer: true,
                      source: 'missing-items-upload',
                      originalFileName: file.name
                    },
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                  });
                }

                resolve();
              } catch (error) {
                reject(error);
              }
            }
          );
        });
      }

      toast.success(`${files.length} fájl sikeresen feltöltve`);
      onUploadComplete(uploadedFiles);
      handleClose();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Hiba történt a feltöltés során');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadProgress({});
    setIsDragging(false);
    onClose();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75" 
          onClick={handleClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Hiányzó dokumentumok feltöltése
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Húzd ide a fájlokat vagy válaszd ki őket tallózással
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Drag & Drop Area */}
              <div
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500'
                } ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <input
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  disabled={uploading}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className={`flex flex-col items-center ${uploading ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <DocumentArrowUpIcon className={`h-12 w-12 mb-3 ${isDragging ? 'text-primary-500' : 'text-gray-400'}`} />
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    {isDragging ? 'Engedd el a fájlokat ide' : 'Húzd ide a fájlokat vagy kattints a tallózáshoz'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Több fájl is feltölthető egyszerre
                  </p>
                </label>
              </div>

              {/* File list */}
              {files.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Kiválasztott fájlok ({files.length})
                    </p>
                    {!uploading && (
                      <button
                        onClick={() => setFiles([])}
                        className="text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Összes törlése
                      </button>
                    )}
                  </div>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {files.map((file, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                      >
                        <div className="flex items-center space-x-3 flex-1 min-w-0">
                          <DocumentArrowUpIcon className="h-5 w-5 text-primary-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatFileSize(file.size)}
                            </p>
                            {uploadProgress[file.name] !== undefined && (
                              <div className="mt-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-xs text-gray-600 dark:text-gray-400">
                                    {uploadProgress[file.name] === 100 ? 'Feltöltve' : 'Feltöltés...'}
                                  </span>
                                  <span className="text-xs font-medium text-primary-600 dark:text-primary-400">
                                    {Math.round(uploadProgress[file.name])}%
                                  </span>
                                </div>
                                <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                                  <div
                                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${uploadProgress[file.name]}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        {!uploading && (
                          <button
                            onClick={() => handleRemoveFile(index)}
                            className="ml-3 p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors flex-shrink-0"
                            title="Fájl eltávolítása"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleUpload}
              disabled={uploading || files.length === 0}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? 'Feltöltés...' : 'Feltöltés'}
            </button>
            <button
              onClick={handleClose}
              disabled={uploading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Mégse
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MissingItemUploadModal;
